import { useCallback, useEffect, useState } from 'react'
import { Box, Checkbox, Message, Text } from 'ui'
import { track } from 'lib/amplitude'
import { useCompanyLocation } from 'hooks/useCompanyLocation'
import { useIsDrivingPosition } from '../DetailsPage/useIsDrivingPosition'
import { useShiftEditor } from '../../useShiftEditor'
import { useGateValue } from '@statsig/react-bindings'
import { FeatureGates } from 'lib/statsig/feature_gates'
import { useAuthContext } from 'components/auth'

interface CaliforniaDrivingAcknowledgementProps {
  onAcknowledgementChange: (isAcknowledged: boolean) => void
}

export function LegacyCaliforniaDrivingAcknowledgement({
  onAcknowledgementChange,
}: CaliforniaDrivingAcknowledgementProps) {
  const { shiftData } = useShiftEditor()
  const location = useCompanyLocation(shiftData?.details?.locationId)
  const isDrivingPosition = useIsDrivingPosition(
    shiftData?.details?.positionId ?? 0
  )
  const { company } = useAuthContext()
  const enableProp22ShiftEditorAffirmation = useGateValue(
    FeatureGates.EnableProp22ShiftEditorAffirmation
  )

  const isCaliforniaDrivingShift =
    enableProp22ShiftEditorAffirmation &&
    isDrivingPosition &&
    location?.address?.state === 'CA' &&
    !company?.w2

  const [isAcknowledged, setIsAcknowledged] = useState(true)

  useEffect(() => {
    if (isCaliforniaDrivingShift) {
      track('impression, ShiftEditor_CA_driving_acknowledgement')
    }
  }, [isCaliforniaDrivingShift])

  useEffect(() => {
    onAcknowledgementChange(!isCaliforniaDrivingShift || isAcknowledged)
  }, [isCaliforniaDrivingShift, isAcknowledged, onAcknowledgementChange])

  const handleCheckboxChange = useCallback((checked: boolean) => {
    setIsAcknowledged(checked)
    track('click, ShiftEditor_CA_driving_acknowledgement_toggle', {
      acknowledged: checked,
    })
  }, [])

  if (!isCaliforniaDrivingShift) {
    return null
  }

  return (
    <Box mt={4}>
      <Message variant={'info'} hideIcon py={3} px={3}>
        <Checkbox value={isAcknowledged} onChange={handleCheckboxChange}>
          <Text fontWeight={2}>
            I acknowledge these California requirements for driving shifts.
          </Text>
        </Checkbox>
        <Box mt={2}>
          <Text>
            The following rules apply to all California 1099 delivery driver
            shifts:
          </Text>
        </Box>
        <Box
          as="ul"
          mt={2}
          mb={0}
          style={{ paddingLeft: '15px', listStyleType: 'disc' }}
        >
          <li style={{ marginBottom: '7.5px' }}>
            Delivery drivers may only perform delivery-related activities during
            delivery shifts (e.g., selecting and collecting the items to be
            delivered and delivering the selected items to customers)
          </li>
          <li style={{ marginBottom: '7.5px' }}>
            All delivery stops within the shift must be within a 50-mile radius
            of the pick-up location.
          </li>
          <li style={{ marginBottom: '7.5px' }}>
            Drivers can be offered the opportunity to complete more than one
            route on the shift.
          </li>
          <li style={{ marginBottom: '7.5px' }}>
            Each route must consist of no more than 12 distinct orders placed by
            different customers
          </li>
          <li>
            Drivers must be allowed to voluntarily decide whether to accept or
            decline each delivery route request offered to them on a shift, and
            no adverse action can be taken against a driver who decides to
            decline a delivery route request
          </li>
        </Box>
      </Message>
      {!isAcknowledged && (
        <Message variant={'warning'} mt={3}>
          You must acknowledge and adhere to the above driving shift
          requirements to post California driving shifts to be performed by
          independent contractors. If you have any questions or need help,
          please reach out to WorkWhile Support or your Account Manager.
        </Message>
      )}
    </Box>
  )
}
